/**
 * Permission & Role Checks Composable
 * NEW PERMISSION SYSTEM (v2):
 * - Permissions are structured as { resource: { action: boolean } }
 * - Use can(resource, action) for permission checks
 * - Role metadata is still used for admin/godmode checks
 */

import type { PermissionMap, UserDataResponse } from '#shared/types/api'

// Extended user data with new permissions format
interface UserDataWithPermissions extends UserDataResponse {
  permissions: PermissionMap
}

/**
 * Composable for checking user permissions based on the new Role-Permission Matrix
 */
export const usePermissions = () => {
  const userData = useState<UserDataWithPermissions | null>('auth-user-data', () => null)

  // ==========================================================================
  // NEW PERMISSION SYSTEM
  // ==========================================================================

  /**
   * Check if user has a specific permission
   * Primary method for the new permission system
   *
   * @param resource - Resource name (e.g., 'camps', 'users', 'finance')
   * @param action - Action name (e.g., 'create', 'read', 'update', 'delete', 'manage')
   * @returns boolean - true if user has permission
   *
   * @example
   * can('camps', 'create') // Check if user can create camps
   * can('finance', 'manage') // Check if user can manage all finance
   */
  const can = (resource: string, action: string): boolean => {
    if (!userData.value?.permissions)
      return false

    if (userData.value.roles?.includes('godmode'))
      return true

    if (resource === 'admin_panel' && action === 'access') {
      const perms = userData.value.permissions

      if (perms.admin_panel?.access)
        return true

      if (perms.system?.read || perms.system?.manage)
        return true

      return Object.keys(perms).some(key => perms[key]?.manage || perms[key]?.read)
    }

    const resourcePerms = userData.value.permissions[resource]
    if (!resourcePerms)
      return false

    // Check exact permission or if they have 'manage' (which includes all actions)
    return resourcePerms[action] || (action !== 'manage' && resourcePerms.manage) || false
  }

  /**
   * Check if user has any of the specified permissions
   *
   * @example
   * canAny([
   *   { resource: 'camps', action: 'read' },
   *   { resource: 'events', action: 'read' }
   * ])
   */
  const canAny = (permissions: Array<{ resource: string, action: string }>): boolean => {
    return permissions.some(p => can(p.resource, p.action))
  }

  /**
   * Check if user has all of the specified permissions
   *
   * @example
   * canAll([
   *   { resource: 'camps', action: 'create' },
   *   { resource: 'camps', action: 'update' }
   * ])
   */
  const canAll = (permissions: Array<{ resource: string, action: string }>): boolean => {
    return permissions.every(p => can(p.resource, p.action))
  }

  /**
   * Get all permissions for a specific resource
   *
   * @example
   * getResourcePermissions('camps') // { create: true, read: true, update: true, delete: false }
   */
  const getResourcePermissions = (resource: string): Record<string, boolean> => {
    return userData.value?.permissions?.[resource] || {}
  }

  /**
   * Check if user can manage a resource (has full CRUD access)
   */
  const canManage = (resource: string): boolean => {
    return can(resource, 'manage')
  }

  // ==========================================================================
  // ROLE-BASED CHECKS (Backwards Compatibility)
  // ==========================================================================

  /**
   * Check if user has a specific role
   */
  const hasRole = (roleName: string): boolean => {
    if (!userData.value?.roles)
      return false

    return userData.value.roles.includes(roleName)
  }

  /**
   * Check if user has any of the specified roles
   */
  const hasAnyRole = (roleNames: string[]): boolean => {
    if (!userData.value?.roles)
      return false

    return roleNames.some(role => userData.value!.roles.includes(role))
  }

  /**
   * Check if user has all of the specified roles
   */
  const hasAllRoles = (roleNames: string[]): boolean => {
    if (!userData.value?.roles)
      return false

    return roleNames.every(role => userData.value!.roles.includes(role))
  }

  /**
   * Get user's current security level
   */
  const securityLevel = computed(() => userData.value?.security_level || 0)

  // ==========================================================================
  // CONVENIENCE COMPUTED PROPERTIES
  // ==========================================================================

  /**
   * Check if user is godmode (ultimate system access)
   */
  const isGodmode = computed(() => hasRole('godmode'))

  /**
   * Check if user is admin (includes godmode)
   */
  const isAdmin = computed(() => hasAnyRole(['admin', 'godmode']))

  /**
   * Check if user is manager or above
   */
  const isManager = computed(() => hasAnyRole(['admin', 'manager', 'godmode']))

  /**
   * Check if user can access admin panel
   * Based on new permission system: system:read or any manage permission
   */
  const canAccessAdmin = computed(() => {
    // Check for system:read or system:manage
    if (can('system', 'read') || can('system', 'manage'))
      return true

    // Or if they have any manage permissions at all
    const perms = userData.value?.permissions || {}
    for (const resource of Object.keys(perms)) {
      if (perms[resource]?.manage || perms[resource]?.read)
        return true
    }

    return false
  })

  /**
   * Check if user can manage camps
   */
  const canManageCamps = computed(() => can('camps', 'manage'))

  /**
   * Check if user can manage users
   */
  const canManageUsers = computed(() => can('users', 'manage'))

  /**
   * Check if user can manage finance
   */
  const canManageFinance = computed(() => can('finance', 'manage'))

  /**
   * Check if user can view reports
   */
  const canViewReports = computed(() => can('reports', 'read') || can('reports', 'manage'))

  return {
    // User data
    userData,

    // NEW: Permission checks (use these!)
    can,
    canAny,
    canAll,
    canManage,
    getResourcePermissions,

    // Role checks
    hasRole,
    hasAnyRole,
    hasAllRoles,

    // Security level
    securityLevel,

    // Computed helpers
    isGodmode,
    isAdmin,
    isManager,
    canAccessAdmin,
    canManageCamps,
    canManageUsers,
    canManageFinance,
    canViewReports,
  }
}
